import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import backend from "../../backend";
import useTitle from "../../hooks/useTitle";

import {
  fmtCLP,
  fmtTime,
  Card,
  DetalleOCs,
  DetalleFacturado,
  DetalleRegularizar,
} from "./ProduccionV2.detalles";
import {
  DetalleOTs,
  ConfirmarAccion,
  UndoBanner,
  SyncCatalogoPrompt,
} from "./ProduccionV2.interacciones";

const UNDO_TIMEOUT_SEC = 30;

// F5.9 — Helper: lista de meses YYYY-MM desde 2025-01 hasta el mes actual + 1
const buildMonthOptions = () => {
  const out = [];
  const now = new Date();
  const startY = 2025, startM = 1;
  const endY = now.getFullYear(), endM = now.getMonth() + 1;
  for (let y = endY; y >= startY; y--) {
    const fromM = (y === startY) ? startM : 1;
    const toM   = (y === endY)   ? endM   : 12;
    for (let m = toM; m >= fromM; m--) {
      out.push(`${y}-${m.toString().padStart(2, "0")}`);
    }
  }
  return out;
};
const MONTH_OPTIONS = buildMonthOptions();
const MONTH_LABELS = {
  "01": "Ene", "02": "Feb", "03": "Mar", "04": "Abr",
  "05": "May", "06": "Jun", "07": "Jul", "08": "Ago",
  "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dic",
};
const fmtMonthLabel = ym => {
  const [y, m] = ym.split("-");
  return `${MONTH_LABELS[m] || m} ${y}`;
};

const ProduccionV2 = () => {
  useTitle("Producción");
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCard, setActiveCard] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // F5.9 — selector de mes (null = vista actual / activas globales)
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Cache local de detalles por card key.
  const [detalle, setDetalle] = useState({});
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState(null);
  const [otsFilter, setOtsFilter] = useState(null);
  const [otsSub, setOtsSub] = useState("all"); // F5.9 — chips: all | con_oc | sin_oc
  const [regularizarSub, setRegularizarSub] = useState("all"); // por regularizar: all | sin_oc | con_oc

  // Estado global de interacciones F4.4
  const [confirmAction, setConfirmAction] = useState(null);
  const [undoBanner, setUndoBanner] = useState(null);
  const [syncPrompt, setSyncPrompt] = useState(null);

  // ─── Helpers de fetch ────────────────────────────────────────────────────
  const buildIssuesUrl = (card, opts = {}) => {
    const { force = false, filter, sub } = opts;
    const params = [`card=${card}`];
    if (card === "ots" && filter) params.push(`filter=${filter}`);
    if (card === "ots" && sub && sub !== "all") params.push(`sub=${sub}`);
    if (card === "porRegularizar" && sub && sub !== "all") params.push(`sub=${sub}`);
    if (selectedMonth) params.push(`month=${selectedMonth}`);
    if (force) params.push("nocache=1");
    return `produccion/issues?${params.join("&")}`;
  };

  const loadDetalle = async (card, opts = {}) => {
    const { force = false } = opts;
    if (!card) return;
    if (!force && detalle[card]) return;
    try {
      setLoadingDetalle(true);
      setErrorDetalle(null);
      const url = buildIssuesUrl(card, opts);
      const { data } = await backend.get(url);
      setDetalle((prev) => ({ ...prev, [card]: data }));
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || "Error desconocido";
      setErrorDetalle(msg);
      console.error("[ProduccionV2] loadDetalle error:", e);
    } finally {
      setLoadingDetalle(false);
    }
  };

  const load = async (nocache = false) => {
    try {
      if (nocache) setRefreshing(true);
      else setLoading(true);
      setError(null);
      const params = [];
      if (selectedMonth) params.push(`month=${selectedMonth}`);
      if (nocache)       params.push("nocache=1");
      const qs = params.length ? `?${params.join("&")}` : "";
      const { data } = await backend.get(`produccion/summary${qs}`);
      setSummary(data);
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || "Error desconocido";
      setError(msg);
      console.error("[ProduccionV2] load error:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    if (nocache && activeCard) {
      await loadDetalle(activeCard, {
        force: true,
        filter: activeCard === "ots" ? otsFilter : undefined,
        sub: activeCard === "ots" ? otsSub : undefined,
      });
    }
  };

  useEffect(() => {
    load();
    // F5.9 — recargar al cambiar mes; limpiar cache de detalles
    setDetalle({});
    setActiveCard(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  // F5.9 — al cambiar chip con OC / sin OC, recargar detalle de ots
  useEffect(() => {
    if (activeCard === "ots") {
      loadDetalle("ots", { force: true, filter: otsFilter, sub: otsSub });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otsSub]);

  // Por regularizar — al cambiar chip, recargar detalle
  useEffect(() => {
    if (activeCard === "porRegularizar") {
      loadDetalle("porRegularizar", { force: true, sub: regularizarSub });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regularizarSub]);

  // ─── F4.4 · Mutaciones de procesos ───────────────────────────────────────

  const refetchAfterMutation = async () => {
    // Summary tiene cache invalidado server-side (ya invalidamos en backend);
    // pero también queremos refetch local.
    await load(true);
  };

  const handlePipelineDefined = (pi, updatedPi, catalogSynced, requestedSync) => {
    // Sync prompt solo aplica a piezas con catálogo (no custom).
    if (pi.piece) {
      setSyncPrompt({
        pieceId: pi.piece?._id || pi.piece,
        pieceName: pi.piece?.name || pi.pieceNameSnapshot,
        processes: updatedPi.processes,
        catalogSynced,
        requestedSync,
      });
    }
    refetchAfterMutation();
  };

  const callPatchProc = async (pi, idx, action, message) => {
    try {
      const { data } = await backend.patch(
        `pieceInstance/${pi._id}/processes/${idx}`,
        { action }
      );
      const updated = data.pieceInstance;
      const proc = updated.processes[idx];

      // Si fue complete: arma undo banner por 30s
      if (action === "complete") {
        const now = Date.now();
        setUndoBanner({
          piId: String(pi._id),
          procIndex: idx,
          procName: proc.name,
          message: `"${proc.name}" completado${
            updated.processes[idx + 1] && updated.processes[idx + 1].startedAt
              ? " · siguiente arrancado"
              : ""
          }`,
          expiresAt: now + UNDO_TIMEOUT_SEC * 1000,
          timestamp: now,
        });
      }
      // Si fue uncomplete: cerrar undo banner si estaba abierto
      if (action === "uncomplete") {
        setUndoBanner(null);
      }

      await refetchAfterMutation();
      return updated;
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || e.message;
      console.error("[callPatchProc]", action, msg);
      alert(`Error: ${msg}`);
    }
  };

  const handleProcAction = (pi, idx, proc, done, inProgress) => {
    if (done) {
      // Solo permitido si banner activo (uncomplete = undo)
      if (undoBanner?.piId === String(pi._id) && undoBanner.procIndex === idx) {
        setConfirmAction({
          message: `Deshacer "${proc.name}" como completado?`,
          onConfirm: async () => {
            setConfirmAction(null);
            await callPatchProc(pi, idx, "uncomplete");
          },
        });
      }
      return;
    }
    if (inProgress) {
      const isLast = idx === pi.processes.length - 1;
      setConfirmAction({
        message: `Marcar "${proc.name}" como completado?`,
        detail: isLast
          ? "Es el último proceso. La pieza pasa a estado lista para entrega."
          : "Auto-arranca el siguiente proceso del pipeline.",
        onConfirm: async () => {
          setConfirmAction(null);
          await callPatchProc(pi, idx, "complete");
        },
      });
      return;
    }
    // pending → start
    setConfirmAction({
      message: `Iniciar "${proc.name}"?`,
      onConfirm: async () => {
        setConfirmAction(null);
        await callPatchProc(pi, idx, "start");
      },
    });
  };

  // ─── Render: loading / error / null ─────────────────────────────────────
  if (loading) {
    return (
      <div className="prod-screen" style={{ background: "#1a1a1a", minHeight: "100vh" }}>
        <div className="prod-header" style={{ padding: "10px 20px", borderBottom: "1px solid #2e2e2e", background: "#1a1a1a" }}>
          <div className="prod-title" style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Producción</div>
          <div style={{ fontSize: 11, color: "#999", marginTop: 1 }}>cargando…</div>
        </div>
        <div className="grid prod-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)", gap: 14, padding: "clamp(10px, 2.5vw, 20px)" }}>
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="prod-card"
              style={{
                background: "#232323",
                border: "1px solid #2e2e2e",
                borderRadius: 12,
                padding: "16px 18px",
                minHeight: 130,
                opacity: 0.55,
              }}
            >
              <div style={{ height: 10, width: "40%", background: "#2e2e2e", borderRadius: 2 }} />
              <div style={{ height: 24, width: "55%", background: "#2e2e2e", borderRadius: 3, marginTop: 12 }} />
              <div style={{ height: 10, width: "70%", background: "#2e2e2e", borderRadius: 2, marginTop: 10 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="prod-screen" style={{ background: "#1a1a1a", minHeight: "100vh" }}>
        <div className="prod-header" style={{ padding: "10px 20px", borderBottom: "1px solid #2e2e2e", background: "#1a1a1a" }}>
          <div className="prod-title" style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Producción</div>
        </div>
        <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-start" }}>
          <div style={{ color: "#e74c3c", fontSize: 12 }}>Error al cargar producción: {error}</div>
          <button
            onClick={() => load(true)}
            style={{
              background: "#1e1e1e",
              border: "1px solid #333",
              color: "#ccc",
              padding: "6px 12px",
              borderRadius: 6,
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!summary) return null;

  const cards = [
    {
      key: "ocs",
      label: "OCs activas",
      value: summary.ocs_activas?.total ?? 0,
      subtitle: `sin facturar · ${fmtCLP(summary.ocs_activas?.monto_total || 0)}`,
      accentColor: "#3498db",
      distribution: [
        { label: "con OC formal", value: summary.ocs_activas?.con_oc_formal || 0, color: "#3498db" },
        { label: "verbales", value: summary.ocs_activas?.verbales || 0, color: "#a78bfa" },
      ],
    },
    {
      key: "ots",
      label: "Items activos",
      // F5.9 — total = items (PieceInstances) en producción. Cuenta items con
      // CO activa + items sinOC/manual/interno. Excluye cotizaciones legacy.
      value: summary.ots_activas?.total ?? 0,
      subtitle: (() => {
        const bo = summary.ots_activas?.by_origin || {};
        const wo = summary.ots_activas?.wo_breakdown?.total || 0;
        return `${bo.con_oc || 0} con OC · ${bo.sin_oc || 0} sin OC · en ${wo} OTs`;
      })(),
      accentColor: "#f39c12",
      distribution: [
        { label: "con OC",  value: summary.ots_activas?.by_origin?.con_oc || 0, color: "#3498db" },
        { label: "sin OC",  value: summary.ots_activas?.by_origin?.sin_oc || 0, color: "#a78bfa" },
      ],
    },
    {
      key: "porRegularizar",
      label: "Por regularizar",
      value: summary.por_regularizar?.guias_count ?? 0,
      subtitle: (() => {
        const monto = summary.por_regularizar?.monto_total || 0;
        const sinOc = summary.por_regularizar?.sin_oc || 0;
        const conOc = summary.por_regularizar?.con_oc || 0;
        const maxDias = summary.por_regularizar?.antiguedad_max_dias || 0;
        return `${fmtCLP(monto)} · ${sinOc} sin OC · ${conOc} con OC${maxDias > 0 ? ` · ${maxDias}d max` : ""}`;
      })(),
      accentColor: "#a78bfa",
      distribution: [
        { label: "sin OC", value: summary.por_regularizar?.sin_oc || 0, color: "#a78bfa" },
        { label: "con OC", value: summary.por_regularizar?.con_oc || 0, color: "#3498db" },
      ],
    },
    {
      key: "facturado",
      label: (() => {
        // F5.9 — usa summary.month si hay vista histórica; si no, mes actual.
        const ym = summary.month
          ? summary.month
          : `${new Date().getUTCFullYear()}-${String(new Date().getUTCMonth() + 1).padStart(2, "0")}`;
        const [y, m] = ym.split("-").map(Number);
        const monthName = new Date(Date.UTC(y, m - 1, 1)).toLocaleString("es-CL", { month: "long", timeZone: "UTC" });
        return `Facturado mes · ${monthName} ${y}`;
      })(),
      value: fmtCLP(summary.facturado_mes?.total_monto || 0),
      subtitle: (() => {
        const cant = summary.facturado_mes?.cantidad_facturas || 0;
        const vs = summary.facturado_mes?.vs_mes_anterior_pct;
        const vsTxt =
          vs === null || vs === undefined
            ? "sin comparativo"
            : vs > 0
            ? `↑ ${vs}% vs mes ant.`
            : vs < 0
            ? `↓ ${Math.abs(vs)}% vs mes ant.`
            : "= vs mes ant.";
        return `neto s/IVA · ${cant} factura${cant === 1 ? "" : "s"} · ${vsTxt}`;
      })(),
      accentColor: "#2ecc71",
      distribution: null,
    },
  ];

  return (
    <div className="prod-screen" style={{ background: "#1a1a1a", minHeight: "100vh" }}>
      {/* Header */}
      <div
        className="prod-header"
        style={{
          padding: "10px 20px",
          borderBottom: "1px solid #2e2e2e",
          background: "#1a1a1a",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div>
          <div className="prod-title" style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>
            Producción
            {summary.isHistorical && (
              <span style={{
                fontSize: 11, fontWeight: 600, color: "#a78bfa",
                marginLeft: 8, padding: "2px 8px", borderRadius: 9,
                background: "#a78bfa22", border: "1px solid #a78bfa55",
              }}>
                📅 vista histórica · {fmtMonthLabel(summary.month)}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: "#999", marginTop: 1 }}>
            actualizado {fmtTime(summary.computedAt)} · vista por pieza
            {summary.cacheHit && (
              <span style={{ color: "#aaa", marginLeft: 6 }}>(caché)</span>
            )}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* F5.9 — Selector de mes */}
          <select
            value={selectedMonth || ""}
            onChange={e => setSelectedMonth(e.target.value || null)}
            style={{
              background: "#1e1e1e", border: "1px solid #333",
              color: "#ccc", borderRadius: 6, padding: "6px 8px",
              fontSize: 11, cursor: "pointer",
            }}
            title="Ver histórico de un mes específico"
          >
            <option value="">Vista actual (activas)</option>
            {MONTH_OPTIONS.map(ym => (
              <option key={ym} value={ym}>{fmtMonthLabel(ym)}</option>
            ))}
          </select>
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            style={{
              background: "#1e1e1e",
              border: "1px solid #333",
              color: refreshing ? "#666" : "#ccc",
              padding: "6px 12px",
              borderRadius: 6,
              fontSize: 11,
              cursor: refreshing ? "wait" : "pointer",
            }}
          >
            {refreshing ? "Recalculando…" : "↻ Recalcular"}
          </button>
        </div>
      </div>

      {/* 3 Cards */}
      {/* padding con clamp: 20px en desktop; en móvil manda .prod-grid (lenguaje 26-ago) */}
      <div className="grid prod-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)", gap: 14, padding: "clamp(10px, 2.5vw, 20px)" }}>
        {cards.map((c) => (
          <Card
            key={c.key}
            label={c.label}
            value={c.value}
            subtitle={c.subtitle}
            accentColor={c.accentColor}
            distribution={c.distribution}
            isActive={activeCard === c.key}
            onClick={() => {
              const next = activeCard === c.key ? null : c.key;
              setActiveCard(next);
              if (next) {
                const opts = {};
                if (next === "ots" && otsFilter) opts.filter = otsFilter;
                if (next === "ots") opts.sub = otsSub;
                if (next === "porRegularizar") opts.sub = regularizarSub;
                loadDetalle(next, opts);
              }
            }}
          />
        ))}
      </div>

      {/* Sync prompt persistente arriba del expand */}
      {syncPrompt && (
        <SyncCatalogoPrompt
          pieceName={syncPrompt.pieceName}
          processes={syncPrompt.processes}
          catalogSynced={syncPrompt.catalogSynced}
          requestedSync={syncPrompt.requestedSync}
          onClose={() => setSyncPrompt(null)}
          onEditCatalog={syncPrompt.pieceId ? () => {
            setSyncPrompt(null);
            navigate("/pieces/new", { state: { _id: syncPrompt.pieceId } });
          } : null}
        />
      )}

      {/* Render condicional del detalle */}
      {activeCard === "ocs" && (
        <DetalleOCs
          items={detalle.ocs?.items || []}
          loading={loadingDetalle}
          error={errorDetalle}
          onWorkOrderClosed={() => load(true)}  /* F5.8 — refresca tras cerrar OT */
        />
      )}
      {activeCard === "ots" && (
        <>
          {/* F5.9 — chips Con OC / Sin OC */}
          <div className="chips-scroll" style={{
            padding: "8px 20px 0 20px",
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            background: "#1a1a1a",
            borderBottom: "1px solid #2e2e2e",
          }}>
            {[
              { key: "all",    label: `Todas (${(summary.ots_activas?.by_origin?.con_oc||0)+(summary.ots_activas?.by_origin?.sin_oc||0)})`, color: "#999" },
              { key: "con_oc", label: `Con OC (${summary.ots_activas?.by_origin?.con_oc||0})`, color: "#3498db" },
              { key: "sin_oc", label: `Sin OC / por cotización (${summary.ots_activas?.by_origin?.sin_oc||0})`, color: "#a78bfa" },
            ].map(c => {
              const active = otsSub === c.key;
              return (
                <button
                  key={c.key}
                  className="chip-touch t-02 no-touch-min"
                  onClick={() => setOtsSub(c.key)}
                  style={{
                    // chip estándar 28-ago: activo = color semántico en texto/borde
                    // sobre `${color}22`; inactivo = #1e1e1e / #999 / borde #333
                    padding: "5px 12px",
                    borderRadius: 6,
                    border: `1px solid ${active ? c.color : "#333"}`,
                    background: active ? `${c.color}22` : "#1e1e1e",
                    color: active ? c.color : "#999",
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          <DetalleOTs
            items={detalle.ots?.items || []}
            loading={loadingDetalle}
            error={errorDetalle}
            currentFilter={otsFilter}
            onFilterChange={(filter) => {
              setOtsFilter(filter);
              loadDetalle("ots", { force: true, filter, sub: otsSub });
            }}
            onProcAction={handleProcAction}
            onPipelineDefined={handlePipelineDefined}
            onSourcingChanged={refetchAfterMutation}
            onEditBOM={(pieceId) => navigate("/pieces/new", { state: { _id: pieceId } })}
            undoBanner={undoBanner}
          />
        </>
      )}
      {activeCard === "porRegularizar" && (
        <DetalleRegularizar
          items={detalle.porRegularizar?.items || []}
          loading={loadingDetalle}
          error={errorDetalle}
          currentSub={regularizarSub}
          onSubChange={setRegularizarSub}
          onChanged={() => load(true)}
        />
      )}
      {activeCard === "facturado" && (
        <DetalleFacturado
          items={detalle.facturado?.items || []}
          loading={loadingDetalle}
          error={errorDetalle}
          totalMonto={summary.facturado_mes?.total_monto || 0}
          cantidad={summary.facturado_mes?.cantidad_facturas || 0}
          topCliente={summary.facturado_mes?.top_cliente || null}
          onWorkOrderEdited={() => load(true)}
        />
      )}

      {/* Globales: confirmación + undo banner */}
      <ConfirmarAccion
        open={!!confirmAction}
        message={confirmAction?.message}
        detail={confirmAction?.detail}
        onConfirm={confirmAction?.onConfirm}
        onCancel={() => setConfirmAction(null)}
      />
      <UndoBanner
        banner={undoBanner}
        onUndo={async () => {
          if (!undoBanner) return;
          await callPatchProc(
            { _id: undoBanner.piId },
            undoBanner.procIndex,
            "uncomplete"
          );
        }}
        onClose={() => setUndoBanner(null)}
        timeoutSec={UNDO_TIMEOUT_SEC}
      />
    </div>
  );
};

export default ProduccionV2;
